import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  SearchIcon,
  ListIcon,
  StarIcon,
  LayoutGridIcon,
} from 'lucide-react';
import { TemplateCard } from './TemplateCard';
import { TemplateEmptyState } from './TemplateEmptyState';
import { useTemplates } from '@/hooks/useTemplates';
import { useCategories } from '@/hooks/useCategories';
import { useTemplateStore } from '@/stores/templateStore';
import { useDebounce } from '@/hooks/useDebounce';

export function CustomTemplatesTab() {
  const navigate = useNavigate();

  // Local state for filters
  const [searchInput, setSearchInput] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Debounce search to reduce API calls
  const debouncedSearch = useDebounce(searchInput, 300);

  // Global UI state from Zustand
  const { viewMode, toggleViewMode } = useTemplateStore();

  const handleCreateClick = () => {
    navigate('/templates/create');
  };

  // Fetch templates with filters
  const { data, isLoading, isError, refetch } = useTemplates({
    search: debouncedSearch,
    category_id: selectedCategoryId,
    is_favorite: showFavoritesOnly || undefined,
    limit: 50,
    sort_by: 'updated_at',
    sort_order: 'desc',
  });

  // Fetch categories for filter
  const { data: categoriesData } = useCategories();
  const categories = categoriesData || [];

  const templates = data?.templates || [];
  const total = data?.total || 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl p-5 shadow-sm animate-pulse"
          >
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
            <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
          <h3 className="text-lg font-semibold text-[#212121] mb-2">
            Unable to load templates
          </h3>
          <p className="text-gray-600 mb-4">
            Please check your connection and try again.
          </p>
          <button
            onClick={() => refetch()}
            className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#4F7A74] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Filters Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="p-4">
          {/* Title and View Toggle */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-base font-medium text-[#212121]">
              {total > 0 && (
                <span className="text-sm text-gray-500">({total} templates)</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* View Mode Toggle */}
              <button
                onClick={toggleViewMode}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              >
                {viewMode === 'grid' ? (
                  <ListIcon className="w-5 h-5" />
                ) : (
                  <LayoutGridIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <SearchIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search templates by name or content..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            {/* Favorites Filter */}
            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors flex items-center gap-1 ${
                showFavoritesOnly
                  ? 'bg-[#5B8E87] text-white'
                  : 'bg-white text-gray-600 border border-gray-300'
              }`}
            >
              <StarIcon className={`w-4 h-4 ${showFavoritesOnly ? 'fill-white' : ''}`} />
              Favorites
            </button>

            {/* All Categories */}
            <button
              onClick={() => setSelectedCategoryId(undefined)}
              className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors ${
                !selectedCategoryId
                  ? 'bg-[#5B8E87] text-white'
                  : 'bg-white text-gray-600 border border-gray-300'
              }`}
            >
              All
            </button>

            {/* Category Filters */}
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategoryId(category.id)}
                className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors ${
                  selectedCategoryId === category.id
                    ? 'bg-[#5B8E87] text-white'
                    : 'bg-white text-gray-600 border border-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {templates.length === 0 ? (
          <TemplateEmptyState
            hasFilters={!!debouncedSearch || !!selectedCategoryId || showFavoritesOnly}
            onCreateClick={handleCreateClick}
            onClearFilters={() => {
              setSearchInput('');
              setSelectedCategoryId(undefined);
              setShowFavoritesOnly(false);
            }}
          />
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                : 'space-y-3'
            }
          >
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                viewMode={viewMode}
              />
            ))}
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      <button
        onClick={handleCreateClick}
        className="fixed bottom-24 right-6 bg-[#5B8E87] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#4F7A74] transition-all active:scale-95 z-30"
      >
        <PlusIcon className="w-6 h-6" />
      </button>
    </>
  );
}
